/**
 * Versions (People Part 6): `/version` on load, on every live reconnect and every five minutes.
 * When the build changes, the shell's bar says a new version is ready — never an automatic reload
 * (festival week most of all). The reload keeps what was being typed: every field on the page is
 * put in sessionStorage under the path and put back after the page comes up.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';

export interface Version {
  version: string;
  build: string;
  deployedAt: string;
}
const EVERY_MS = 5 * 60_000;
const DRAFT_KEY = (path: string): string => `cider.draft:${path}`;

/** Every input, textarea and select with an id or name on the page, keyed by it. */
export function saveDrafts(): void {
  const out: Record<string, string> = {};
  for (const el of document.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >('input, textarea, select')) {
    const key = el.id || el.name;
    if (!key) continue;
    if (el instanceof HTMLInputElement && (el.type === 'password' || el.type === 'file')) continue;
    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
      out[key] = el.checked ? 'on' : '';
      continue;
    }
    if (el.value) out[key] = el.value;
  }
  try {
    sessionStorage.setItem(DRAFT_KEY(window.location.pathname), JSON.stringify(out));
  } catch {
    /* nothing kept, nothing lost */
  }
}

/** Put the saved values back once the page's fields exist; forget them after. */
export function restoreDrafts(): void {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(DRAFT_KEY(window.location.pathname));
    if (raw) sessionStorage.removeItem(DRAFT_KEY(window.location.pathname));
  } catch {
    return;
  }
  if (!raw) return;
  const drafts = new Map(Object.entries(JSON.parse(raw) as Record<string, string>));
  let tries = 0;
  const attempt = (): void => {
    let left = 0;
    for (const [key, value] of drafts) {
      const el =
        document.getElementById(key) ?? document.querySelector(`[name="${CSS.escape(key)}"]`);
      if (!(
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement
      )) {
        left++;
        continue;
      }
      // React owns the field: the native setter through the prototype (so React sees the change), then its events.
      const proto =
        el instanceof HTMLInputElement
          ? HTMLInputElement.prototype
          : el instanceof HTMLTextAreaElement
            ? HTMLTextAreaElement.prototype
            : HTMLSelectElement.prototype;
      if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio'))
        el.checked = value === 'on';
      else Reflect.set(proto, 'value', value, el);
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
      drafts.delete(key);
    }
    if (left > 0 && tries++ < 20) window.setTimeout(attempt, 250);
  };
  window.setTimeout(attempt, 250);
}

export function useVersion(): {
  current: Version | null;
  fresh: Version | null;
  check: () => void;
  reload: () => void;
} {
  const [current, setCurrent] = useState<Version | null>(null);
  const [fresh, setFresh] = useState<Version | null>(null);
  const check = useCallback(() => {
    api<Version>('GET', '/version')
      .then((v) => {
        setCurrent((c) => {
          if (!c) return v;
          if (c.build !== v.build || c.version !== v.version) setFresh(v);
          return c;
        });
      })
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    check();
    const t = window.setInterval(check, EVERY_MS);
    // Coming back to the tab is a good moment too.
    const onFocus = (): void => {
      if (document.visibilityState === 'visible') check();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.clearInterval(t);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [check]);
  const reload = useCallback(() => {
    saveDrafts();
    window.location.reload();
  }, []);
  return { current, fresh, check, reload };
}
